/**
 * useResizeObserver — Lightweight hook to measure element size.
 * Returns [ref, size] where size is { width, height } (0,0 until mounted).
 */

import * as React from "react";

export function useResizeObserver<T extends HTMLElement = HTMLDivElement>(): [
  React.RefObject<T | null>,
  { width: number; height: number }
] {
  const ref = React.useRef<T | null>(null);
  const [size, setSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });

    observer.observe(el);
    setSize({ width: el.offsetWidth, height: el.offsetHeight });
    return () => observer.disconnect();
  }, []);

  return [ref, size];
}
